import clsx from "clsx";
import React, { useMemo } from "react";
import { IconButton, IconChevronLeft, IconChevronRight, Paragraph } from "@trainline/depot-web";
import dayjs from "../../../utils/dayjs";
import DateButton from "../DateButton";
import createDateCells from "../utils/createDateCells";
import getDateString from "../utils/getDateString";
import * as styles from "./Calendar.module.css";
const DefaultPrevButton = (props) => /* @__PURE__ */ React.createElement(IconButton, {
  ...props,
  className: styles.calendarButton,
  type: "button",
  icon: IconChevronLeft,
  iconSize: 24,
  size: "large",
  contained: true
});
const DefaultNextButton = (props) => /* @__PURE__ */ React.createElement(IconButton, {
  ...props,
  className: styles.calendarButton,
  type: "button",
  size: "large",
  icon: IconChevronRight,
  iconSize: 24,
  contained: true
});
var PriceStatus = /* @__PURE__ */ ((PriceStatus2) => {
  PriceStatus2["NOT_REQUIRED"] = "notRequired";
  PriceStatus2["NO_PRICE"] = "noPrice";
  PriceStatus2["CHEAPEST"] = "cheapest";
  PriceStatus2["NORMAL"] = "normal";
  return PriceStatus2;
})(PriceStatus || {});
const Calendar = ({
  intlMessages,
  className,
  style,
  locale,
  showDaysOutsideOfMonth = false,
  selectedValue,
  focusedValue,
  min,
  max,
  isDisabled,
  onPrevClick,
  onNextClick,
  onChange,
  onKeyboardNavigation,
  setDateCellRef,
  renderPrevButton: PrevButtonSlot = DefaultPrevButton,
  renderNextButton: NextButtonSlot = DefaultNextButton,
  renderDateButton: DateButtonSlot = DateButton
}) => {
  dayjs.locale(locale);
  const target = useMemo(() => {
    return dayjs(getDateString(dayjs(focusedValue).startOf("month")));
  }, [focusedValue]);
  const weeks = useMemo(() => {
    return createDateCells(target, min, max);
  }, [target, min, max]);
  const isPrevMonthInAllowedRange = !min || target.subtract(1, "month").endOf("month").isSameOrAfter(min, "date");
  const isNextMonthInAllowedRange = !max || target.add(1, "month").startOf("month").isSameOrBefore(max, "date");
  return /* @__PURE__ */ React.createElement("div", {
    style,
    className: clsx(styles.calendar, className),
    "aria-roledescription": "datetime picker"
  }, /* @__PURE__ */ React.createElement("div", {
    className: styles.screenReaderOnly
  }, intlMessages == null ? void 0 : intlMessages["Calendar.initialInstructions.ariaLabel"]), /* @__PURE__ */ React.createElement("div", {
    className: styles.calendarHeader,
    role: "none"
  }, /* @__PURE__ */ React.createElement(PrevButtonSlot, {
    "aria-disabled": isDisabled || !isPrevMonthInAllowedRange,
    onClick: onPrevClick,
    "data-testid": "calendar-navigate-to-previous-month",
    "aria-label": intlMessages == null ? void 0 : intlMessages["Calendar.previousMonthButton.ariaLabel"]
  }), /* @__PURE__ */ React.createElement(Paragraph, {
    "aria-live": "polite",
    fontWeight: "semibold",
    typeStyle: "body",
    className: styles.calendarHeaderTitle
  }, /* @__PURE__ */ React.createElement("time", {
    role: "none",
    dateTime: `${target.format("MM-YYYY")}`,
    id: "datetime-picker-label"
  }, target.local().format("MMMM YYYY"))), /* @__PURE__ */ React.createElement(NextButtonSlot, {
    "aria-disabled": isDisabled || !isNextMonthInAllowedRange,
    onClick: onNextClick,
    "data-testid": "calendar-navigate-to-next-month",
    "aria-label": intlMessages == null ? void 0 : intlMessages["Calendar.nextMonthButton.ariaLabel"]
  })), /* @__PURE__ */ React.createElement("div", {
    className: styles.calendarMonth,
    tabIndex: -1,
    role: "none",
    onKeyDown: onKeyboardNavigation
  }, /* @__PURE__ */ React.createElement("table", {
    className: styles.calendarGrid,
    "data-testid": `calendar-month-${target.format("YYYY-MM")}`,
    role: "grid",
    "aria-label": intlMessages == null ? void 0 : intlMessages["Calendar.title.ariaLabel"],
    "aria-labelledby": "datetime-picker-label"
  }, /* @__PURE__ */ React.createElement("thead", {
    "aria-hidden": true
  }, /* @__PURE__ */ React.createElement("tr", null, dayjs.weekdaysMin(true).map((weekday) => /* @__PURE__ */ React.createElement("th", {
    key: weekday,
    className: styles.calendarWeekday,
    scope: "col"
  }, weekday)))), /* @__PURE__ */ React.createElement("tbody", null, weeks.map((week) => /* @__PURE__ */ React.createElement("tr", {
    key: `week-${week[0].date.week().toString()}`
  }, week.map((day) => /* @__PURE__ */ React.createElement("td", {
    key: day.key,
    "data-date": day.date.local().format("L"),
    role: "none"
  }, showDaysOutsideOfMonth || !showDaysOutsideOfMonth && day.isCurrentMonth ? /* @__PURE__ */ React.createElement(DateButtonSlot, {
    value: getDateString(day.date),
    isFocused: day.date.isSame(focusedValue, "date"),
    isSelected: day.date.isSame(selectedValue, "date"),
    isDisabled: isDisabled || !!(min && day.date.isBefore(min, "date")) || !!(max && day.date.isAfter(max, "date")),
    onChange,
    getAriaLabel: intlMessages["Calendar.DateButton.ariaLabel"],
    ref: (instance) => setDateCellRef(getDateString(day.date), instance)
  }) : null))))))));
};
var Calendar_default = Calendar;
export {
  PriceStatus,
  Calendar_default as default
};
